import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Empty,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { CoefficientSource, ICycleMatiere } from 'app/entities/cycle/cycle.reducer';
import { scoringModeLabels } from 'app/shared/model/cycle.model';
import {
  ClasseRow,
  CoefficientsState,
  CycleMatieresState,
  CycleRow,
  MatiereRow,
  SetClasses,
  SetCoefficients,
  SetCycleMatieres,
  SetSousMatieres,
  ScoringModesByCycleState,
  SousMatiereRow,
  SousMatieresState,
  pairKey,
  tmpId,
} from './pedagogie-wizard.types';

interface CycleSelectorProps {
  activeCycleId?: string;
  disabled?: boolean;
  existingCycleOptions: CycleRow[];
  setActiveCycleId: (cycleId: string) => void;
}

export const CycleSelector = ({ activeCycleId, disabled = false, existingCycleOptions, setActiveCycleId }: CycleSelectorProps) =>
  existingCycleOptions.length ? (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text type="secondary">Cycle en cours de configuration</Typography.Text>
      <Select
        value={activeCycleId}
        onChange={setActiveCycleId}
        style={{ minWidth: 280 }}
        showSearch
        optionFilterProp="children"
        disabled={disabled}
      >
        {existingCycleOptions.map(cycle => (
          <Select.Option key={cycle.id} value={cycle.id}>
            {cycle.nom} - {cycle.scoringMode ? scoringModeLabels[cycle.scoringMode] : 'Mode non défini'}
          </Select.Option>
        ))}
      </Select>
    </Space>
  ) : (
    <Result
      status="info"
      title="Créez d'abord un cycle"
      subTitle="La configuration des classes et matières démarre une fois le cycle créé."
    />
  );

interface ClassesStepProps {
  activeCycle?: CycleRow;
  activeCycleId?: string;
  classeStepRows: ClasseRow[];
  existingCycleOptions: CycleRow[];
  loading: boolean;
  selectedClasseIds: string[];
  setActiveCycleId: (cycleId: string) => void;
  setClasses: SetClasses;
  setSelectedClasseIds: (ids: string[]) => void;
}

export const ClassesStep = ({
  activeCycle,
  activeCycleId,
  classeStepRows,
  existingCycleOptions,
  loading,
  selectedClasseIds,
  setActiveCycleId,
  setClasses,
  setSelectedClasseIds,
}: ClassesStepProps) => (
  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
    <CycleSelector activeCycleId={activeCycleId} disabled existingCycleOptions={existingCycleOptions} setActiveCycleId={setActiveCycleId} />
    <Alert
      type="info"
      showIcon
      message="Cochez les classes à rattacher à ce cycle. Les classes déjà liées peuvent être décochées pour les retirer du cycle."
    />
    <Table
      rowKey="localId"
      dataSource={classeStepRows}
      pagination={false}
      loading={loading}
      size="small"
      rowSelection={{
        selectedRowKeys: selectedClasseIds,
        onChange: selectedKeys => setSelectedClasseIds(selectedKeys.map(String)),
      }}
      columns={[
        {
          title: 'Classe',
          dataIndex: 'nom',
          render: (_, record: ClasseRow) => (
            <Input
              value={record.nom}
              disabled
              onChange={event =>
                setClasses(current => current.map(row => (row.localId === record.localId ? { ...row, nom: event.target.value } : row)))
              }
            />
          ),
        },
        {
          title: 'Statut',
          dataIndex: 'status',
          width: 130,
          render: (_, record: ClasseRow) =>
            record.cycleId === activeCycle?.id ? <Tag color="green">Liée au cycle</Tag> : <Tag color="blue">Non liée</Tag>,
        },
      ]}
    />
  </Space>
);

interface MatieresStepProps {
  activeCycle?: CycleRow;
  activeCycleId?: string;
  cycleMatieres: CycleMatieresState;
  ecoleMatieres: ICycleMatiere[];
  existingCycleOptions: CycleRow[];
  setActiveCycleId: (cycleId: string) => void;
  setCycleMatieres: SetCycleMatieres;
  sousMatieres: SousMatieresState;
}

export const MatieresStep = ({
  activeCycle,
  activeCycleId,
  cycleMatieres,
  ecoleMatieres,
  existingCycleOptions,
  setActiveCycleId,
  setCycleMatieres,
  sousMatieres,
}: MatieresStepProps) => {
  const cycleId = activeCycle?.id;
  const rows = cycleId ? cycleMatieres[cycleId] || [] : [];
  const linkedIds = new Set(
    rows
      .filter(row => !row.deleted)
      .map(row => row.matiereId || row.id)
      .filter(Boolean)
  );
  const available = ecoleMatieres.filter(matiere => !linkedIds.has(matiere.id));

  const linkMatiere = (matiereId: string) => {
    const matiere = ecoleMatieres.find(row => row.id === matiereId);
    if (!cycleId || !matiere) return;
    setCycleMatieres(current => ({
      ...current,
      [cycleId]: [
        ...(current[cycleId] || []),
        {
          ...matiere,
          localId: tmpId(),
          cycleId,
          matiereId,
          status: 'link',
          avecSousMatieres: Boolean(matiere.avecSousMatieres),
        },
      ],
    }));
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <CycleSelector activeCycleId={activeCycleId} existingCycleOptions={existingCycleOptions} setActiveCycleId={setActiveCycleId} />
      {cycleId && (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space style={{ width: '100%' }} wrap>
            <Select
              placeholder="Lier une matière existante"
              style={{ minWidth: 260 }}
              showSearch
              allowClear
              optionFilterProp="children"
              onChange={value => value && linkMatiere(value)}
            >
              {available.map(matiere => (
                <Select.Option key={matiere.id} value={matiere.id}>
                  {matiere.nom}
                </Select.Option>
              ))}
            </Select>
          </Space>
          <Table
            rowKey="localId"
            dataSource={rows.filter(row => !row.deleted)}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Matière',
                dataIndex: 'nom',
                render: (_, record: MatiereRow) => record.nom,
              },
              {
                title: 'Sous-matières',
                dataIndex: 'avecSousMatieres',
                width: 170,
                render(_, record: MatiereRow) {
                  const hasChildren = (sousMatieres[pairKey(cycleId, record.id)] || []).some(row => !row.deleted);
                  return (
                    <span title={hasChildren ? "Supprimez d'abord les sous-matières pour décocher cette option" : undefined}>
                      <Checkbox
                        checked={Boolean(record.avecSousMatieres)}
                        disabled={Boolean(record.avecSousMatieres) && hasChildren}
                        onChange={event =>
                          setCycleMatieres(current => ({
                            ...current,
                            [cycleId]: rows.map(row =>
                              row.localId === record.localId
                                ? {
                                    ...row,
                                    avecSousMatieres: event.target.checked,
                                    status: row.status === 'existing' ? 'update' : row.status,
                                  }
                                : row
                            ),
                          }))
                        }
                      >
                        Oui
                      </Checkbox>
                    </span>
                  );
                },
              },
              {
                title: 'Statut',
                dataIndex: 'status',
                width: 130,
                render: status => (
                  <Tag color={status === 'existing' ? 'green' : 'orange'}>{status === 'existing' ? 'Liée' : 'À sauvegarder'}</Tag>
                ),
              },
              {
                title: '',
                width: 64,
                render: (_, record: MatiereRow) => (
                  <Popconfirm
                    title="Retirer cette matière du cycle ?"
                    okText="Oui"
                    cancelText="Non"
                    onConfirm={() =>
                      setCycleMatieres(current => ({
                        ...current,
                        [cycleId]:
                          record.status === 'existing' || record.status === 'update'
                            ? rows.map(row => (row.localId === record.localId ? { ...row, deleted: true } : row))
                            : rows.filter(row => row.localId !== record.localId),
                      }))
                    }
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                ),
              },
            ]}
          />
        </Space>
      )}
    </Space>
  );
};

interface SousMatieresStepProps {
  activeCycleId?: string;
  cycles: CycleRow[];
  existingCycleOptions: CycleRow[];
  matieresWithSousMatieres: MatiereRow[];
  setActiveCycleId: (cycleId: string) => void;
  setSousMatieres: SetSousMatieres;
  sousMatieres: SousMatieresState;
}

export const SousMatieresStep = ({
  activeCycleId,
  cycles,
  existingCycleOptions,
  matieresWithSousMatieres,
  setActiveCycleId,
  setSousMatieres,
  sousMatieres,
}: SousMatieresStepProps) => {
  const addSousMatiere = (matiere: MatiereRow) => {
    if (!matiere.cycleMatiereId) {
      return;
    }
    const key = pairKey(matiere.cycleId, matiere.id);
    const id = tmpId();
    setSousMatieres(current => ({
      ...current,
      [key]: [...(current[key] || []), { id, localId: id, nom: '', cycleMatiereId: matiere.cycleMatiereId, status: 'new' }],
    }));
  };

  if (!matieresWithSousMatieres.length) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <CycleSelector activeCycleId={activeCycleId} existingCycleOptions={existingCycleOptions} setActiveCycleId={setActiveCycleId} />
        <Result
          status="info"
          title="Aucune sous-matière à configurer"
          subTitle="Cochez une matière comme ayant des sous-matières à l'étape précédente."
        />
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <CycleSelector activeCycleId={activeCycleId} existingCycleOptions={existingCycleOptions} setActiveCycleId={setActiveCycleId} />
      {matieresWithSousMatieres.map(matiere => {
        const key = pairKey(matiere.cycleId, matiere.id);
        const rows = sousMatieres[key] || [];
        const cycle = cycles.find(item => item.id === matiere.cycleId);
        return (
          <div key={key}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {matiere.nom} <Typography.Text type="secondary">({cycle?.nom})</Typography.Text>
              </Typography.Title>
              <Button icon={<PlusOutlined />} onClick={() => addSousMatiere(matiere)} disabled={!matiere.cycleMatiereId}>
                Ajouter une sous-matière
              </Button>
              <Table
                rowKey="localId"
                dataSource={rows.filter(row => !row.deleted)}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Nom',
                    dataIndex: 'nom',
                    render: (_, record: SousMatiereRow) => (
                      <Input
                        value={record.nom}
                        onChange={event =>
                          setSousMatieres(current => ({
                            ...current,
                            [key]: rows.map(row => (row.localId === record.localId ? { ...row, nom: event.target.value } : row)),
                          }))
                        }
                      />
                    ),
                  },
                  {
                    title: 'Statut',
                    dataIndex: 'status',
                    width: 130,
                    render: status => (
                      <Tag color={status === 'existing' ? 'green' : 'orange'}>{status === 'existing' ? 'Existante' : 'Nouvelle'}</Tag>
                    ),
                  },
                  {
                    title: '',
                    width: 64,
                    render: (_, record: SousMatiereRow) => (
                      <Popconfirm
                        title="Supprimer cette sous-matière ?"
                        okText="Oui"
                        cancelText="Non"
                        onConfirm={() =>
                          setSousMatieres(current => ({
                            ...current,
                            [key]:
                              record.status === 'new'
                                ? rows.filter(row => row.localId !== record.localId)
                                : rows.map(row => (row.localId === record.localId ? { ...row, deleted: true } : row)),
                          }))
                        }
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            </Space>
            <Divider />
          </div>
        );
      })}
    </Space>
  );
};

interface CoefficientsStepProps {
  activeCycle?: CycleRow;
  activeCycleClasses: ClasseRow[];
  activeCycleId?: string;
  activeCycleMatieres: MatiereRow[];
  activeCycleUsesCoefficients: boolean;
  coefficients: CoefficientsState;
  existingCycleOptions: CycleRow[];
  setActiveCycleId: (cycleId: string) => void;
  setCoefficients: SetCoefficients;
  sousMatieres: SousMatieresState;
  sousCoefficients: CoefficientsState;
  setSousCoefficients: SetCoefficients;
  coefficientSources: Record<string, CoefficientSource>;
  setCoefficientSources: React.Dispatch<React.SetStateAction<Record<string, CoefficientSource>>>;
}

export const CoefficientsStep = ({
  activeCycle,
  activeCycleClasses,
  activeCycleId,
  activeCycleMatieres,
  activeCycleUsesCoefficients,
  coefficients,
  existingCycleOptions,
  setActiveCycleId,
  setCoefficients,
  sousMatieres,
  sousCoefficients,
  setSousCoefficients,
  coefficientSources,
  setCoefficientSources,
}: CoefficientsStepProps) => {
  if (!activeCycleUsesCoefficients) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <CycleSelector activeCycleId={activeCycleId} existingCycleOptions={existingCycleOptions} setActiveCycleId={setActiveCycleId} />
        <Result status="info" title="Non applicable" subTitle="Ce cycle utilise le mode Pondération, sans coefficients." />
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <CycleSelector activeCycleId={activeCycleId} existingCycleOptions={existingCycleOptions} setActiveCycleId={setActiveCycleId} />
      <div>
        <Typography.Title level={4}>{activeCycle?.nom}</Typography.Title>
        {activeCycleMatieres.length === 0 || activeCycleClasses.length === 0 ? (
          <Empty description="Ajoutez au moins une matière et une classe pour saisir les coefficients" />
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Le choix s'applique à toutes les classes du cycle. Il sera enregistré à la validation de cette étape. Les coefficients de l'autre stratégie sont conservés."
            />
            {activeCycleMatieres.map(matiere => {
              const children = (sousMatieres[pairKey(activeCycle.id, matiere.id)] || []).filter(row => !row.deleted && row.actif !== false);
              const source = coefficientSources[matiere.cycleMatiereId] || 'MATIERE';
              const detailed = source === 'SOUS_MATIERES';
              const values = detailed ? sousCoefficients : coefficients;
              const setValues = detailed ? setSousCoefficients : setCoefficients;
              const rows = detailed ? children : [matiere];
              return (
                <div key={matiere.id}>
                  <Typography.Title level={5}>{matiere.nom}</Typography.Title>
                  {matiere.avecSousMatieres && children.length > 0 && (
                    <Radio.Group
                      aria-label={`Stratégie de ${matiere.nom}`}
                      value={source}
                      onChange={event => setCoefficientSources(current => ({ ...current, [matiere.cycleMatiereId]: event.target.value }))}
                      style={{ marginBottom: 12 }}
                    >
                      <Radio value="MATIERE">Coefficient général</Radio>
                      <Radio value="SOUS_MATIERES">Coefficients des sous-matières</Radio>
                    </Radio.Group>
                  )}
                  {detailed && children.length === 0 && (
                    <Alert
                      type="error"
                      message="Aucune sous-matière active. Choisissez le coefficient général."
                      action={
                        <Button onClick={() => setCoefficientSources(current => ({ ...current, [matiere.cycleMatiereId]: 'MATIERE' }))}>
                          Coefficient général
                        </Button>
                      }
                    />
                  )}
                  <Table<{ id: string; nom: string }>
                    rowKey="id"
                    dataSource={rows}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                    columns={[
                      { title: detailed ? 'Sous-matière' : 'Matière', dataIndex: 'nom', fixed: 'left', width: 180 },
                      ...activeCycleClasses.map(classe => ({
                        title: classe.nom,
                        key: classe.id,
                        width: 140,
                        render(_, row: { id: string; nom: string }) {
                          return (
                            <InputNumber
                              aria-label={`${matiere.nom} - ${row.nom} - ${classe.nom}`}
                              min={1}
                              precision={0}
                              value={values[row.id]?.[classe.id] ?? undefined}
                              onChange={value =>
                                setValues(current => ({
                                  ...current,
                                  [row.id]: { ...current[row.id], [classe.id]: typeof value === 'number' ? value : null },
                                }))
                              }
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      })),
                    ]}
                  />
                </div>
              );
            })}
          </Space>
        )}
        <Divider />
      </div>
    </Space>
  );
};

interface SummaryStepProps {
  activeCycle?: CycleRow;
  classesByCycle: Record<string, ClasseRow[]>;
  cycleMatieres: CycleMatieresState;
  scoringModesByCycle: ScoringModesByCycleState;
  sousMatieres: SousMatieresState;
}

export const SummaryStep = ({ activeCycle, classesByCycle, cycleMatieres, scoringModesByCycle, sousMatieres }: SummaryStepProps) => (
  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
    <Result
      status="success"
      title="Configuration pédagogique prête"
      subTitle="Les données sauvegardées restent accessibles dans la gestion des cycles."
    />
    <Table
      rowKey="id"
      dataSource={activeCycle ? [activeCycle] : []}
      pagination={false}
      size="small"
      columns={[
        { title: 'Cycle', dataIndex: 'nom' },
        { title: 'Classes', render: (_, cycle: CycleRow) => (classesByCycle[cycle.id] || []).length },
        { title: 'Matières', render: (_, cycle: CycleRow) => (cycleMatieres[cycle.id] || []).length },
        {
          title: 'Sous-matières',
          render: (_, cycle: CycleRow) =>
            Object.keys(sousMatieres)
              .filter(key => key.startsWith(`${cycle.id}:`))
              .reduce((total, key) => total + sousMatieres[key].filter(row => !row.deleted).length, 0),
        },
        {
          title: 'Coefficients',
          render: (_, cycle: CycleRow) =>
            scoringModesByCycle[cycle.id]?.usesCoefficients ? <Tag color="green">Configurés</Tag> : <Tag>Non applicable</Tag>,
        },
      ]}
    />
  </Space>
);
